/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { daysFromNow, parseDateTime } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';
import { Price } from '@zapier/currency';

import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import { BannerProps } from './types';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';

export const shouldRender = (data: Result['data']) => {
  const { currentAccount } = data;
  return (
    currentAccount.isTrialingCompanies &&
    currentAccount.companiesTrialEndDate &&
    ['owner', 'admin', 'super_admin'].includes(currentAccount.currentUserRole)
  );
};

export const Component = function CompaniesTrialing(props: BannerProps) {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const { data, currency } = props;
  const { currentAccount } = data;
  const {
    companiesTrialEndDate,
    companiesTrialAutoUpgrade: willAutoUpgrade,
  } = currentAccount;
  const trialDaysRemaining = daysFromNow(companiesTrialEndDate!);
  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
  const title = `${trialDaysRemaining} ${daysText} left in your Company trial`;
  // @ref ul-1: Divide by 12 since `nextCompanyPlan` should always be annual.
  const nextPlanAmount = (data.nextCompanyPlan?.amount || 0) / 12;

  const formattedEndDate = parseDateTime(companiesTrialEndDate!)
    .toLocaleString('en-US', { month: 'short', day: 'numeric' })
    .replace(/([a-z]+)/i, '$1.');

  const autoBillingLink = (
    <Link href={getFullyQualifiedURL('/app/settings/billing')}>
      Edit trial payment settings
    </Link>
  );

  const content = willAutoUpgrade ? (
    <p>
      {`You will be billed and charged `}
      <Price currency={currency}>{nextPlanAmount}</Price>
      /month {' at the end of your trial. '}
      {autoBillingLink}
    </p>
  ) : (
    <p>
      {`On ${formattedEndDate}, you’ll lose access to Company plan features and be downgraded to a ${
        data.currentAccount?.plan?.type ?? 'Free'
      } plan. `}
      {autoBillingLink}
    </p>
  );

  return (
    <Alert layout={props.layout} title={title} type="info">
      {content}
    </Alert>
  );
};
